import styles from './PartnerContactForm.module.scss';

import FormControl from '@material-ui/core/FormControl';
import Grid, { GridSpacing } from '@material-ui/core/Grid';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import type { AxiosInstance } from 'axios';
import { Field, Formik } from 'formik';
import { stringify } from 'qs';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { HelperText } from 'components/Form/HelperText';
import { SubmitButton } from 'components/Form/SubmitButton';
import { ValidationError } from 'components/Form/ValidationError';
import { ModalHeader } from 'components/Modal/Header/ModalHeader';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { Environment } from 'utils/Environment';
import { getObjectKeyLength } from 'utils/Object';
import { PARTNER_CONTACT_FORM_TEST_ID } from './PartnerContactForm.const';

interface IFormValues {
  email?: string;
  companyName?: string;
  name?: string;
  description?: string;
  partner?: string;
}

type FormAttributes = React.FormHTMLAttributes<HTMLFormElement>;
interface IFormProps extends Omit<FormAttributes, 'onSubmit'> {
  onSuccess: () => void;
  onError: () => void;
  onRequestClose: () => void;
  cosApiClient?: AxiosInstance;
}

// Rename keys to those required by the COS endpoint
function renameKeys(values: IFormValues) {
  return {
    'entry.1081727053': values.email,
    'entry.313553393': values.companyName,
    'entry.1794104129': values.name,
    'entry.1597887207': values.description,
    'entry.386926779': values.partner,
  };
}

interface IPartnerContactFormProps extends IFormValues, IFormProps, WithTranslation {}

const requestUrl = '/v2/internal/dashboard/form/waitlist';

const PartnerContactForm: React.FunctionComponent<IPartnerContactFormProps> = ({
  email,
  companyName,
  name,
  description,
  partner,
  t,
  i18n,
  tReady,
  onRequestClose,
  onSuccess,
  onError,
  cosApiClient = Environment.current.cosApiClient,
  ...props
}) => {
  const validationMessages = {
    required: t('required'),
    validEmail: t('valid-email'),
  };
  const { browser } = useBreakpoints();
  const mobile = browser.is.mobile;

  return (
    <>
      <ModalHeader
        heading={t('partner-contact-form')}
        subheading={t('partner-contact-instruction', { partner })}
        hasDivider
        onCloseButtonClick={onRequestClose}
      />
      <Formik
        isInitialValid={false}
        initialValues={{
          email: email || '',
          companyName: companyName || '',
          name: name || '',
          description: description || '',
          partner: partner || '',
        }}
        onSubmit={(values, actions) => {
          const data = stringify({
            version: 'partnerContact',
            values: stringify(renameKeys(values)),
          });
          const config = {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          };
          cosApiClient
            .post(requestUrl, data, config)
            .then(onSuccess)
            .catch(onError)
            .then(() => actions.setSubmitting(false));
        }}
        validationSchema={() =>
          Yup.object().shape({
            email: Yup.string()
              .trim()
              .email(() => validationMessages.validEmail)
              .required(() => validationMessages.required),
            companyName: Yup.string()
              .trim()
              .max(50)
              .required(() => validationMessages.required),
            name: Yup.string()
              .trim()
              .max(50)
              .required(() => validationMessages.required),
            description: Yup.string()
              .trim()
              .required(() => validationMessages.required),
            partner: Yup.string()
              .trim()
              .required(() => validationMessages.required),
          })
        }
        validateOnBlur
      >
        {({
          handleSubmit,
          handleChange,
          // handleBlur,
          setFieldTouched,
          touched,
          values,
          errors,
          isSubmitting,
          dirty,
        }) => (
          <form onSubmit={handleSubmit} {...props}>
            <Field required type="hidden" name="partner" id="partner" label={t('partner')} />
            <Grid
              container
              spacing={
                mobile
                  ? (parseInt(styles.formPaddingMobile) as GridSpacing)
                  : (parseInt(styles.formPadding) as GridSpacing)
              }
            >
              <Grid item xs={12} lg={6}>
                <FormControl fullWidth>
                  <InputLabel htmlFor="name">{t('name')}</InputLabel>
                  <Input
                    data-testid={PARTNER_CONTACT_FORM_TEST_ID.name}
                    id="name"
                    name="name"
                    autoComplete="name"
                    value={values.name}
                    onChange={handleChange}
                    // Remove this hack when Formik "touched" issue is fixed
                    // https://github.com/jaredpalmer/formik/issues/738
                    onBlur={() => setFieldTouched('name', true)}
                  />
                  {touched.name && errors.name ? <ValidationError message={errors.name} /> : <HelperText />}
                </FormControl>
              </Grid>
              <Grid item xs={12} lg={6}>
                <FormControl fullWidth>
                  <InputLabel htmlFor="companyName">{t('company-name')}</InputLabel>
                  <Input
                    data-testid={PARTNER_CONTACT_FORM_TEST_ID.companyName}
                    id="companyName"
                    name="companyName"
                    autoComplete="organization"
                    value={values.companyName}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched('companyName', true)}
                  />
                  {touched.companyName && errors.companyName ? (
                    <ValidationError message={errors.companyName} />
                  ) : (
                    <HelperText />
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel htmlFor="email">{t('email')}</InputLabel>
                  <Input
                    data-testid={PARTNER_CONTACT_FORM_TEST_ID.emailAddress}
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched('email', true)}
                  />
                  {touched.email && errors.email ? (
                    <ValidationError message={errors.email} />
                  ) : (
                    <HelperText />
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel htmlFor="description">{t('partner-contact-describe')}</InputLabel>
                  <Input
                    data-testid={PARTNER_CONTACT_FORM_TEST_ID.description}
                    multiline
                    id="description"
                    name="description"
                    rowsMax={4}
                    value={values.description}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched('description', true)}
                  />
                  {touched.description && errors.description ? (
                    <ValidationError message={errors.description} />
                  ) : (
                    <HelperText />
                  )}
                </FormControl>
              </Grid>
              <Grid className={styles.gridBottom} item xs={12}>
                <SubmitButton
                  data-testid={PARTNER_CONTACT_FORM_TEST_ID.submitButton}
                  disabled={
                    !getObjectKeyLength(values) || !!getObjectKeyLength(errors) || isSubmitting || !dirty
                  }
                >
                  {t('send-message')}
                </SubmitButton>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </>
  );
};

const Translated = withTranslation()(PartnerContactForm);

export { Translated as PartnerContactForm, IFormProps, IFormValues };
